import { useState, useRef } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import ConfirmModal from '/src/components/modal/ConfirmModal/ConfirmModal';
import editorAction from '../../../actions/editorAction';
import { formatDateTime, countExistingBlocks, blocksFromStepMap } from '../utils/historyView';
import { noteRole, scenarioCreatedText, canResumeEntry, buildConversationChain, conversationFromChain } from '../utils/conversation';
import { describeError } from '../engine/errors';

const useTeiaConversation = ({ engine, history, addHistoryEntry, patchHistoryEntry, setSelectedEntryId, existingBlockIds }) => {
    const { applyScenario, removeCategoriesFromCanvas, removeBlocksFromCanvas } = engine;
    const { generateAiBotBuilder } = editorAction();
    const [messages, setMessages] = useState([]);
    const [conversationHistory, setConversationHistory] = useState([]);
    const [draft, setDraft] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [lastRun, setLastRun] = useState(null);
    const conversationTailRef = useRef(null);

    const pushMessage = (role, text, stats = null, historyId = null) => setMessages((previous) => [...previous, { role, text, stats, historyId }]);

    const [sessionUsage, setSessionUsage] = useState({ requests: 0, calls: 0, tokens: 0 });
    const addSessionUsage = (stats) => {
        if (!stats) return;
        setSessionUsage((previous) => ({
            requests: previous.requests + 1,
            calls: previous.calls + stats.calls,
            tokens: previous.tokens + stats.total_tokens,
        }));
    };

    const generateScenario = async (prompt) => {
        const history = [...conversationHistory, { role: 'user', content: prompt }];
        setConversationHistory(history);

        setIsGenerating(true);
        const result = await generateAiBotBuilder(history);

        if (!result) {
            setIsGenerating(false);
            return;
        }

        if (result.status === 'clarification') {
            const entry = addHistoryEntry({ prompt, status: 'clarification', message: result.message, stats: result.stats, previousEntryId: conversationTailRef.current });
            conversationTailRef.current = entry.id;
            addSessionUsage(result.stats);
            pushMessage('assistant', result.message, result.stats, entry.id);
            setConversationHistory([...history, { role: 'assistant', content: result.message }]);
            setIsGenerating(false);
            return;
        }

        const stepMap = {};
        const categoryList = [];
        const groups = result.groups || [];
        const entryFields = {
            prompt,
            groups,
            steps: result.steps,
            notes: result.notes,
            referencedVariables: result.referencedVariables,
            stats: result.stats,
            previousEntryId: conversationTailRef.current,
        };

        try {
            const newlyCreatedNames = await applyScenario(result.steps, result.referencedVariables, stepMap, groups, categoryList);
            const blocks = blocksFromStepMap(stepMap);
            const entry = addHistoryEntry({ ...entryFields, blocks, categories: categoryList });
            conversationTailRef.current = entry.id;

            addSessionUsage(result.stats);
            pushMessage('assistant', scenarioCreatedText(result.steps.length, groups.length), result.stats, entry.id);
            if (newlyCreatedNames.length > 0) {
                pushMessage('assistant', "These (empty) variables were referenced, so I created them automatically: " + newlyCreatedNames.join(', ') + ". Fill them in if needed before using the bot.");
            }
            for (const note of result.notes) {
                pushMessage(noteRole(note), note);
            }
            const assistantRecap = JSON.stringify(groups.length > 0 ? { status: 'ready', groups, steps: result.steps, notes: result.notes } : { status: 'ready', steps: result.steps, notes: result.notes });
            setConversationHistory([...history, { role: 'assistant', content: assistantRecap }]);
            setLastRun({ blockIds: blocks.map((block) => block.blockId), categories: categoryList, historyLength: history.length, entryId: entry.id });
        } catch (err) {
            console.error('[Teia] Creating the blocks failed', err);
            const reason = describeError(err);
            const failedBlocks = blocksFromStepMap(stepMap);
            const entry = addHistoryEntry({ ...entryFields, status: 'failed', error: reason, blocks: failedBlocks, categories: categoryList });
            setLastRun(failedBlocks.length > 0 ? { blockIds: failedBlocks.map((block) => block.blockId), categories: categoryList, historyLength: history.length, entryId: entry.id } : null);
            addSessionUsage(result.stats);
            pushMessage('error', "Creating the blocks failed: " + reason + ". The AI response and any block already created are kept in the history (Teia insights > History).", result.stats, entry.id);
        }

        setIsGenerating(false);
    };

    const replayEntry = async (entry) => {
        if (isGenerating) return;
        setIsGenerating(true);

        const stepMap = {};
        const categoryList = [];
        const entryFields = {
            source: 'replay',
            prompt: entry.prompt,
            groups: entry.groups || [],
            steps: entry.steps,
            notes: entry.notes,
            referencedVariables: entry.referencedVariables,
        };

        try {
            const newlyCreatedNames = await applyScenario(entry.steps, entry.referencedVariables, stepMap, entry.groups || [], categoryList);
            addHistoryEntry({ ...entryFields, blocks: blocksFromStepMap(stepMap), categories: categoryList });
            pushMessage('assistant', 'Scenario re-created from the history: ' + entry.steps.length + ' block' + (entry.steps.length > 1 ? 's' : '') + ' added to the canvas.');
            if (newlyCreatedNames.length > 0) {
                pushMessage('assistant', "These (empty) variables were referenced, so I created them automatically: " + newlyCreatedNames.join(', ') + ".");
            }
        } catch (err) {
            console.error('[Teia] Re-creating the blocks failed', err);
            const reason = describeError(err);
            addHistoryEntry({ ...entryFields, status: 'failed', error: reason, blocks: blocksFromStepMap(stepMap), categories: categoryList });
            pushMessage('error', "Re-creating the blocks failed: " + reason);
        }

        setIsGenerating(false);
    };

    const removeEntryBlocks = async (entry) => {
        if (isGenerating) return;
        const count = countExistingBlocks(entry, existingBlockIds);
        const confirmed = await NiceModal.show(ConfirmModal, {
            title: 'Delete blocks',
            body: 'Delete ' + count + ' block' + (count > 1 ? 's' : '') + ' created by this generation from the canvas?',
            confirmLabel: 'Delete',
            danger: true,
        });
        if (!confirmed) return;

        setIsGenerating(true);
        try {
            await removeCategoriesFromCanvas(entry.categories);
            const removed = await removeBlocksFromCanvas(entry.blocks.map((block) => block.blockId));
            patchHistoryEntry(entry.id, { blocksRemovedAt: new Date().toISOString() });
            if (lastRun && lastRun.entryId === entry.id) setLastRun(null);
            pushMessage('assistant', 'Removed ' + removed + ' block' + (removed > 1 ? 's' : '') + ' from the canvas (generation of ' + formatDateTime(entry.createdAt) + ').');
        } catch (err) {
            console.log(err);
            pushMessage('error', "Removing the blocks failed.");
        }
        setIsGenerating(false);
    };

    const resumeConversation = async (entry) => {
        if (isGenerating || !canResumeEntry(entry)) return;

        const chain = buildConversationChain(entry, history);
        if (messages.length > 0) {
            const confirmed = await NiceModal.show(ConfirmModal, {
                title: 'Resume conversation',
                body: 'Replace the current conversation with this one? (The history and the canvas are not touched.)',
                confirmLabel: 'Resume',
            });
            if (!confirmed) return;
        }

        const { apiHistory, bubbles } = conversationFromChain(chain);
        const exchanges = chain.length;
        const notice = {
            role: 'assistant',
            text: 'Conversation resumed (' + exchanges + ' exchange' + (exchanges > 1 ? 's' : '') + ' up to ' + formatDateTime(entry.createdAt) + '). Teia remembers it: continue below.',
            stats: null,
            historyId: null,
        };

        setConversationHistory(apiHistory);
        setMessages([...bubbles, notice]);
        setSessionUsage(chain.reduce((total, item) => item.stats
            ? { requests: total.requests + 1, calls: total.calls + item.stats.calls, tokens: total.tokens + item.stats.total_tokens }
            : total, { requests: 0, calls: 0, tokens: 0 }));
        conversationTailRef.current = entry.id;

        const hasBlocksOnCanvas = entry.status === 'ready' && countExistingBlocks(entry, existingBlockIds) > 0;
        setLastRun(hasBlocksOnCanvas ? { blockIds: entry.blocks.map((block) => block.blockId), categories: entry.categories || [], historyLength: apiHistory.length - 1, entryId: entry.id } : null);
        setSelectedEntryId(entry.id);
    };

    const sendMessage = () => {
        const text = draft.trim();
        if (text === '' || isGenerating) return;
        pushMessage('user', text);
        setDraft('');
        generateScenario(text);
    };

    const startNewSession = () => {
        if (isGenerating) return;
        conversationTailRef.current = null;
        setMessages([]);
        setConversationHistory([]);
        setLastRun(null);
        setSessionUsage({ requests: 0, calls: 0, tokens: 0 });
        setSelectedEntryId(null);
    };

    const revertLastRun = async () => {
        if (!lastRun || isGenerating) return;
        setIsGenerating(true);

        try {
            await removeCategoriesFromCanvas(lastRun.categories);
            const removed = await removeBlocksFromCanvas(lastRun.blockIds);
            if (lastRun.entryId) patchHistoryEntry(lastRun.entryId, { blocksRemovedAt: new Date().toISOString() });
            setConversationHistory((previous) => previous.slice(0, lastRun.historyLength - 1));
            pushMessage('assistant', 'Last generation reverted: ' + removed + ' block' + (removed > 1 ? 's' : '') + ' removed.');
            setLastRun(null);
        } catch (err) {
            console.log(err);
            pushMessage('error', "Reverting failed.");
        }

        setIsGenerating(false);
    };

    return {
        messages, draft, setDraft, isGenerating, lastRun, sessionUsage, pushMessage,
        sendMessage, startNewSession, revertLastRun, replayEntry, removeEntryBlocks, resumeConversation,
    };
};

export default useTeiaConversation;
